<template>
  <!--
    Filtros de certificaciones (extraído de certificacion-mecanico / certificacion-electrico).
    Componente presentacional: pinta los 5 campos + botón limpiar y EMITE los cambios;
    la orquestación (recargar/filtrar) la mantiene cada padre.

    Comunicación (sin mutar la prop):
      - `filters` (objeto) es de solo lectura aquí; los cambios se emiten con
        `update:filters` → el padre lo enlaza con `:filters.sync`.
      - Eventos:
          @update:filters (nuevoFiltro) → objeto de filtros actualizado (patrón .sync)
          @change              → cualquier cambio (subcontratista/pedido/estado/fecha)
          @id-input (value)    → texto del ID (con debounce, para consumidores server-side como civil)
          @subcontractor-change (partnerId) → para que el padre recalcule work orders si lo necesita
          @clear               → botón limpiar (el padre resetea + recarga)
  -->
  <div class="panel mb-4">
    <div class="flex flex-wrap gap-4">
      <div class="flex-1 min-w-[150px]">
        <label class="block text-xs text-gray-500 mb-1">ID</label>
        <b-input :value="filters.id" :placeholder="$t('all')" size="is-small" @input="onIdInput"></b-input>
      </div>
      <div class="flex-1 min-w-[150px]">
        <label class="block text-xs text-gray-500 mb-1">{{ $t('subcontractor') }}</label>
        <b-select
          :value="filters.subcontractor"
          :placeholder="$t('all')"
          size="is-small"
          expanded
          :disabled="subcontractorDisabled"
          @input="onSubcontractorInput"
        >
          <option :value="null">{{ $t('all') }}</option>
          <option v-for="sub in subcontractors" :key="sub.id" :value="sub.id">{{ sub.name }}</option>
        </b-select>
      </div>
      <div class="flex-1 min-w-[150px]">
        <label class="block text-xs text-gray-500 mb-1">{{ $t('work_order') }}</label>
        <b-select :value="filters.workOrder" :placeholder="$t('all')" size="is-small" expanded @input="onWorkOrderInput">
          <option :value="null">{{ $t('all') }}</option>
          <option v-for="order in workOrders" :key="order.id" :value="order.id">{{ order.code }}</option>
        </b-select>
      </div>
      <div class="flex-1 min-w-[150px]">
        <label class="block text-xs text-gray-500 mb-1">{{ $t('status') }}</label>
        <b-select :value="filters.status" :placeholder="$t('all')" size="is-small" expanded @input="onStatusInput">
          <option :value="null">{{ $t('all') }}</option>
          <option
            v-for="statusOption in statusOptions"
            :key="'status_' + statusOption.value"
            :value="statusOption.value"
          >
            {{ $t(statusOption.label) }}
          </option>
        </b-select>
      </div>
      <div class="flex-1 min-w-[150px]">
        <label class="block text-xs text-gray-500 mb-1">{{ $t('date') }}</label>
        <b-datepicker
          :value="filters.date"
          :placeholder="$t('all')"
          size="is-small"
          :position="datepickerPosition"
          icon="calendar"
          icon-pack="fas"
          :month-names="$t('months_names')"
          :day-names="$t('days_names')"
          :first-day-of-week="parseInt($t('first_day_of_week'))"
          @input="onDateInput"
        ></b-datepicker>
      </div>
    </div>
    <div class="mt-3">
      <b-button size="is-small" type="is-light" icon-left="eraser" icon-pack="fas" @click="onClear">
        {{ $t('clean_filters') }}
      </b-button>
    </div>
  </div>
</template>

<script>
// Opciones de estado del flujo de validación (mismas que civil/mecánico/eléctrico).
// Se exporta para poder reutilizarla como default del prop sin triplicar la constante.
export const VALIDATION_FLOW_STATUS_OPTIONS = Object.freeze([
  { value: 0, label: 'validation_status_pending' },
  { value: 1, label: 'validation_status_pending_validation' },
  { value: 2, label: 'validation_status_completed' },
  { value: 3, label: 'validation_status_rejected' },
  { value: 4, label: 'validation_status_pending_answer' },
  { value: 5, label: 'validation_status_reopened' },
  { value: 6, label: 'validation_status_closed' },
  { value: 7, label: 'validation_status_rejected_partial' },
])

export default {
  name: 'CertificationFilter',

  props: {
    // Objeto de filtros compartido con el padre: { id, subcontractor, workOrder, status, date }.
    filters: {
      type: Object,
      required: true,
    },
    subcontractors: {
      type: Array,
      default: () => [],
    },
    workOrders: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => VALIDATION_FLOW_STATUS_OPTIONS,
    },
    // Solo civil bloquea el subcontratista para colaboradores; mec/elec lo dejan en false.
    subcontractorDisabled: {
      type: Boolean,
      default: false,
    },
    // mec/elec usaban 'is-bottom-left'; civil no ponía posición (deja el default de Buefy).
    datepickerPosition: {
      type: String,
      default: 'is-bottom-left',
    },
    // Debounce (ms) del campo ID, igual que civil.
    idDebounce: {
      type: Number,
      default: 1300,
    },
  },

  data: () => ({
    idDebounceTimer: null,
  }),

  beforeDestroy() {
    if (this.idDebounceTimer) {
      clearTimeout(this.idDebounceTimer)
      this.idDebounceTimer = null
    }
  },

  methods: {
    // Emite el objeto de filtros actualizado sin mutar la prop (patrón .sync).
    _emitUpdate(field, value) {
      this.$emit('update:filters', { ...this.filters, [field]: value })
    },

    // ID: actualiza el filtro al instante (para el filtrado reactivo en cliente) y emite el
    // evento server-side con debounce (para consumidores como civil, que no deben disparar
    // en cada tecla).
    onIdInput(value) {
      this._emitUpdate('id', value)
      if (this.idDebounceTimer) {
        clearTimeout(this.idDebounceTimer)
      }
      this.idDebounceTimer = setTimeout(() => {
        this.$emit('id-input', value)
        this.$emit('change')
      }, this.idDebounce)
    },

    onSubcontractorInput(value) {
      this._emitUpdate('subcontractor', value)
      this.$emit('subcontractor-change', value)
      this.$emit('change')
    },

    onWorkOrderInput(value) {
      this._emitUpdate('workOrder', value)
      this.$emit('change')
    },

    onStatusInput(value) {
      this._emitUpdate('status', value)
      this.$emit('change')
    },

    onDateInput(value) {
      this._emitUpdate('date', value)
      this.$emit('change')
    },

    onClear() {
      this.$emit('clear')
    },
  },
}
</script>

<style lang="scss" scoped>
.panel {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  // El panel no crece más que su contenedor.
  max-width: 100%;
}
</style>
